import { type Component, For, Show, createSignal } from 'solid-js';
import { createQuery, useMutation, useQueryClient } from '@tanstack/solid-query';
import { useNavigate } from '@solidjs/router';
import {
  AlarmClock,
  Bell,
  BellOff,
  CalendarCheck,
  CheckCheck,
  Loader2,
  Megaphone,
  Settings2,
  Swords,
  Users,
  Wallet,
  type LucideProps,
} from 'lucide-solid';
import { notificationsApi, type NotificationCategory, type NotificationItem } from '~/shared/api/notifications-api';
import { queryKeys } from '~/shared/api/query-keys';
import { Popover } from '~/shared/ui/Popover';
import { formatRelativeTime } from '~/shared/lib/formatters';
import { useI18n } from '~/shared/lib/i18n';
import { cn } from '~/shared/lib/cn';
import { resolveNotificationUrl } from '~/shared/lib/notificationRoute';

const CATEGORY_ICON: Record<NotificationCategory, Component<LucideProps>> = {
  booking: CalendarCheck,
  payment: Wallet,
  system: Settings2,
  marketing: Megaphone,
  reminder: AlarmClock,
  social: Users,
  matches: Swords,
};

const CATEGORY_TINT: Record<NotificationCategory, string> = {
  booking: 'var(--color-accent)',
  payment: 'var(--color-success)',
  system: 'var(--color-text-tertiary)',
  marketing: 'var(--color-purple)',
  social: 'var(--color-info, var(--color-accent))',
  matches: 'var(--color-info, var(--color-accent))',
  reminder: 'var(--color-warning)',
};

export const NotificationsMenu: Component = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<HTMLElement>();

  const unread = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: ({ signal }) => notificationsApi.unreadCount({ signal }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  }));

  const list = createQuery(() => ({
    meta: { peripheral: true },
    queryKey: queryKeys.notifications.list(),
    queryFn: ({ signal }) => notificationsApi.list({ limit: 20 }, { signal }),
    enabled: open(),
    retry: false,
  }));

  const markRead = useMutation(() => ({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all() }),
  }));

  const markAll = useMutation(() => ({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all() }),
  }));

  const count = () => unread.data?.count ?? 0;
  const items = () => list.data?.items ?? [];

  const onItemClick = (n: NotificationItem) => {
    if (!n.isRead) markRead.mutate(n.id);
    if (!n.actionUrl) return;
    if (/^https?:\/\//i.test(n.actionUrl)) {
      setOpen(false);
      window.open(n.actionUrl, '_blank', 'noopener');
      return;
    }
    const to = resolveNotificationUrl(n.actionUrl);
    if (to) {
      setOpen(false);
      navigate(to);
    }
  };

  return (
    <>
      <button
        type="button"
        ref={setAnchor}
        class="press relative h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/6 text-secondary"
        aria-label={t('topbar.notifications')}
        aria-haspopup="menu"
        aria-expanded={open()}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell class="h-[18px] w-[18px]" />
        <Show when={count() > 0}>
          <span
            class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none flex items-center justify-center tabular shadow-[0_0_0_2px_var(--color-bg-glass)]"
          >
            {count() > 9 ? '9+' : count()}
          </span>
        </Show>
      </button>

      <Popover open={open()} onClose={() => setOpen(false)} anchor={anchor()} align="end">
        <div class="glass-menu w-[360px] max-h-[440px] flex flex-col overflow-hidden animate-pop-in">
          <div class="shrink-0 flex items-center gap-2 px-3.5 h-11 border-b border-hairline">
            <span class="text-[13px] font-semibold flex-1">{t('topbar.notifications')}</span>
            <Show when={count() > 0}>
              <button
                type="button"
                class="press inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] text-secondary hover:bg-white/6 disabled:opacity-50"
                disabled={markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                <CheckCheck class="h-3.5 w-3.5" />
                {t('topbar.notificationsMarkAllRead')}
              </button>
            </Show>
          </div>

          <div class="flex-1 overflow-y-auto">
            <Show
              when={!list.isLoading}
              fallback={
                <div class="flex items-center justify-center h-32 text-tertiary">
                  <Loader2 class="h-5 w-5 animate-spin" />
                </div>
              }
            >
              <Show
                when={!list.isError}
                fallback={
                  <p class="text-[12.5px] text-tertiary text-center py-10 px-4">
                    {t('topbar.notificationsError')}
                  </p>
                }
              >
                <Show
                  when={items().length > 0}
                  fallback={
                    <div class="flex flex-col items-center justify-center gap-2 py-12 text-tertiary">
                      <BellOff class="h-7 w-7 opacity-50" />
                      <p class="text-[12.5px]">{t('topbar.notificationsEmpty')}</p>
                    </div>
                  }
                >
                  <ul class="py-1">
                    <For each={items()}>
                      {(n) => {
                        const Icon = CATEGORY_ICON[n.category] ?? Bell;
                        const tint = CATEGORY_TINT[n.category] ?? 'var(--color-text-tertiary)';
                        return (
                          <li>
                            <button
                              type="button"
                              class={cn(
                                'press w-full text-left flex gap-3 px-3.5 py-2.5 transition-colors hover:bg-white/5',
                                !n.isRead && 'bg-[color-mix(in_srgb,var(--color-accent)_7%,transparent)]',
                              )}
                              onClick={() => onItemClick(n)}
                            >
                              <span
                                class="mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                                style={{
                                  background: `color-mix(in srgb, ${tint} 16%, transparent)`,
                                  color: tint,
                                }}
                              >
                                <Icon class="h-4 w-4" />
                              </span>
                              <span class="flex-1 min-w-0">
                                <span class="flex items-center gap-2">
                                  <span
                                    class={cn(
                                      'text-[13px] leading-snug flex-1 min-w-0 truncate',
                                      n.isRead ? 'text-secondary font-medium' : 'text-primary font-semibold',
                                    )}
                                  >
                                    {n.title}
                                  </span>
                                  <Show when={!n.isRead}>
                                    <span class="shrink-0 w-2 h-2 rounded-full bg-accent" />
                                  </Show>
                                </span>
                                <Show when={n.body}>
                                  <span class="block text-[12px] text-tertiary leading-snug mt-0.5 line-clamp-2">
                                    {n.body}
                                  </span>
                                </Show>
                                <span class="block text-[11px] text-tertiary mt-1 tabular">
                                  {formatRelativeTime(n.createdAt)}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </Show>
              </Show>
            </Show>
          </div>
        </div>
      </Popover>
    </>
  );
};
